// Purchases hub — one outlet panel with four tabs (desktop Purchases menu
// parity):
//   Receive Stock | Receive Stock List | On Hold Receive Stock List | Summary
//
// Composes:
//   <receive-entry>                          — Receive Stock (SavePurchases)
//   <purchase-records kind="receive|hold">
//   <summary-report kind="purchase">
//
// Recall/Edit mediation: the lists dispatch "trade-recall" (holds) or
// "trade-edit" (posted receives); the hub switches to the Receive Stock tab
// and hands the record to receive-entry. A recalled hold is released only
// after the new post/re-hold succeeds; an edited receive is posted back to the
// same id.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TradePurchaseRow } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import './receive-entry.js';
import './purchase-records.js';
import './summary-report.js';

type PurchasesTab = 'entry' | 'list' | 'hold' | 'summary';

interface KeptView {
  tab: PurchasesTab;
}

@customElement('purchases-page')
export class PurchasesPage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .tabs { margin: 0.75rem 1rem 0; margin-bottom: 0; }
    .pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .pane > * { flex: 1; min-height: 0; }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _tab: PurchasesTab = 'entry';
  @state() private _recall: { row: TradePurchaseRow; token: number; edit?: boolean } | null = null;
  private _recallSeq = 0;

  private _view = new OutletViewCache<KeptView>(this, 'purchases-page', {
    // receive-entry keeps its own per-outlet view (lines + edit id); a stale
    // recall must not overwrite that restored screen.
    capture: () => ({ tab: this._tab }),
    restore: (v) => {
      this._tab = v.tab;
      this._recall = null;
    },
    reset: () => {
      this._tab = 'entry';
      this._recall = null;
    },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode);
  }

  private _onRecall = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      { tradeKind: string; row: TradePurchaseRow; edit?: boolean } | undefined;
    if (!detail || detail.tradeKind !== 'purchase' || !detail.row) return;
    this._recallSeq += 1;
    this._recall = { row: detail.row, token: this._recallSeq, edit: !!detail.edit };
    this._tab = 'entry';
  };

  private _tabLabel(id: PurchasesTab): string {
    switch (id) {
      case 'entry': return 'Receive Stock';
      case 'list': return 'Receive Stock List';
      case 'hold': return 'On Hold Receive Stock';
      default: return 'Summary';
    }
  }

  render() {
    if (!this.storeCode) return html`<div style="padding:2rem;color:var(--color-text-secondary)">No outlet selected.</div>`;
    const tabs: PurchasesTab[] = ['entry', 'list', 'hold', 'summary'];
    return html`
      <div class="tabs">
        ${tabs.map((t) => html`
          <button class=${this._tab === t ? 'active' : ''}            data-testid="purchase-tab" data-tab=${t}            @click=${() => { if (t !== 'entry') this._recall = null; this._tab = t; }}>
            ${this._tabLabel(t)}
          </button>
        `)}
      </div>
      <div class="pane" @trade-recall=${this._onRecall} @trade-edit=${this._onRecall}>
        ${this._tab === 'entry' ? html`
          <receive-entry
            .storeCode=${this.storeCode}
            .recallRow=${this._recall?.row ?? null}
            .recallToken=${this._recall?.token ?? 0}
            .editMode=${this._recall?.edit ?? false}
          ></receive-entry>
        ` : ''}
        ${this._tab === 'list' ? html`
          <purchase-records .storeCode=${this.storeCode} .kind=${'receive'}></purchase-records>
        ` : ''}
        ${this._tab === 'hold' ? html`
          <purchase-records .storeCode=${this.storeCode} .kind=${'hold'}></purchase-records>
        ` : ''}
        ${this._tab === 'summary' ? html`
          <summary-report .storeCode=${this.storeCode} .kind=${'purchase'}></summary-report>
        ` : ''}
      </div>
    `;
  }
}
